/*
 * Landing-page copy in every language Board Bash supports.
 *
 * The locale list and the display names mirror the app's own picker
 * (`packages/frontend-boardfest/src/main-menu/language-content.tsx` in the
 * boardbash repo). Game names and taglines are taken verbatim from the app's
 * `gameNames` block (`src/i18n/locales/*.ts`) so the portal and the game never
 * disagree on what a game is called.
 *
 * A classic script, not a module: it must run before first paint (see apply.js).
 * Keys missing from a locale fall back to English, but `npm run check:i18n`
 * fails the build on a gap, so they should never be missing.
 */ window.NOCAP_I18N = {
    locales: [
        {
            code: 'en',
            name: 'English'
        },
        {
            code: 'de',
            name: 'Deutsch'
        },
        {
            code: 'fr',
            name: "Fran\xe7ais"
        },
        {
            code: 'it',
            name: 'Italiano'
        },
        {
            code: 'es',
            name: "Espa\xf1ol"
        },
        {
            code: 'pl',
            name: 'Polski'
        },
        {
            code: 'cs',
            name: "\u010Ce\u0161tina"
        },
        {
            code: 'mk',
            name: "\u041C\u0430\u043A\u0435\u0434\u043E\u043D\u0441\u043A\u0438"
        }
    ],
    strings: {
        en: {
            meta: {
                title: 'nocap.gg - Pick a game. Play right now.',
                description: 'Browser games we build ourselves. Quick rounds, real opponents, nothing to install.'
            },
            nav: {
                aria: 'Main',
                ourGames: 'Our games'
            },
            hero: {
                titleLine1: 'Pick a game.',
                titleLine2: 'Play right now.',
                cta: 'See our games'
            },
            games: {
                heading: 'Our games'
            },
            boardbash: {
                logoAlt: 'Board Bash logo',
                tagline: 'Classic board games, fast rounds',
                body: 'Tic-Tac-Toe, Four in a Row, Dots & Boxes and Gomoku. Play against the computer, pass the device around, or open a room and go head-to-head with a friend.',
                tagTicTacToe: 'Tic-Tac-Toe',
                tagFourInARow: 'Four in a Row',
                tagDotsAndBoxes: 'Dots & Boxes',
                tagGomoku: 'Gomoku',
                play: 'Play Board Bash',
                storeAria: 'Get Board Bash on Google Play',
                players: "1\u20132 players \xb7 online & local"
            },
            lagerhaus: {
                tagline: 'Warehouse puzzle',
                body: 'Sokoban, done beautifully. Push every crate onto its mark in as few moves as you can, then take your score to the online leaderboard. Endless unique levels, so no two rounds are alike.',
                tagPuzzle: 'Puzzle',
                tagSolo: 'Solo',
                tagLeaderboards: 'Leaderboards',
                play: 'Play Lagerhaus',
                storeAria: 'Get Lagerhaus on Google Play',
                players: "1 player \xb7 web & mobile"
            },
            store: {
                badgeAlt: 'Get it on Google Play'
            },
            workshop: {
                heading: 'More of our games are in the workshop.',
                body: "We only put a game here once we\u2019re proud of it, so new titles land as soon as they\u2019re ready to play."
            },
            footer: {
                games: 'Games',
                privacy: 'Privacy',
                contact: 'Contact',
                madeWith: "Made with \u2764\uFE0F in",
                germanEngineered: 'German-engineered',
                australia: 'Australia',
                legal: 'Google Play and the Google Play logo are trademarks of Google LLC.'
            },
            contact: {
                title: 'Say hi',
                lede: "Pick what it\u2019s about and your email app opens with the subject filled in. A real person reads every message.",
                close: 'Close',
                feedbackTitle: 'Feedback or ideas',
                feedbackBody: "What you love, what you\u2019d change",
                bugTitle: "Something\u2019s broken",
                bugBody: 'Tell us the game and your device',
                dataTitle: 'My data',
                dataBody: 'See or delete what we hold',
                orWrite: 'Or write to us directly:',
                copy: 'Copy',
                copied: 'Copied'
            },
            ui: {
                languageLabel: 'Language'
            }
        },
        de: {
            meta: {
                title: 'nocap.gg - Spiel aussuchen. Sofort spielen.',
                description: 'Browsergames, die wir selbst bauen. Kurze Runden, echte Gegner, nichts zu installieren.'
            },
            nav: {
                aria: 'Hauptnavigation',
                ourGames: 'Unsere Spiele'
            },
            hero: {
                titleLine1: 'Spiel aussuchen.',
                titleLine2: 'Sofort spielen.',
                cta: 'Unsere Spiele ansehen'
            },
            games: {
                heading: 'Unsere Spiele'
            },
            boardbash: {
                logoAlt: 'Board Bash Logo',
                tagline: 'Klassische Brettspiele, schnelle Runden',
                body: "Tic-Tac-Toe, Vier in einer Reihe, K\xe4sek\xe4stchen und Gomoku. Spiel gegen den Computer, gib das Ger\xe4t weiter, oder \xf6ffne einen Raum und tritt gegen Freunde an.",
                tagTicTacToe: 'Tic-Tac-Toe',
                tagFourInARow: 'Vier in einer Reihe',
                tagDotsAndBoxes: "K\xe4sek\xe4stchen",
                tagGomoku: 'Gomoku',
                play: 'Board Bash spielen',
                storeAria: 'Board Bash bei Google Play holen',
                players: "1\u20132 Spieler \xb7 online & lokal"
            },
            lagerhaus: {
                tagline: 'Lager-Puzzle',
                body: "Sokoban, richtig sch\xf6n gemacht. Schiebe jede Kiste mit so wenigen Z\xfcgen wie m\xf6glich auf ihr Ziel und bring deine Punktzahl in die Online-Rangliste. Endlos viele einzigartige Level \u2013 keine Runde gleicht der anderen.",
                tagPuzzle: 'Puzzle',
                tagSolo: 'Solo',
                tagLeaderboards: 'Ranglisten',
                play: 'Lagerhaus spielen',
                storeAria: 'Lagerhaus bei Google Play holen',
                players: "1 Spieler \xb7 Web & Mobil"
            },
            store: {
                badgeAlt: 'Jetzt bei Google Play'
            },
            workshop: {
                heading: 'Weitere Spiele sind in der Werkstatt.',
                body: "Ein Spiel kommt erst hierher, wenn wir stolz darauf sind \u2013 neue Titel erscheinen also, sobald sie spielbereit sind."
            },
            footer: {
                games: 'Spiele',
                privacy: 'Datenschutz',
                contact: 'Kontakt',
                madeWith: "Mit \u2764\uFE0F gemacht in",
                germanEngineered: 'Deutsche Ingenieurskunst',
                australia: 'Australien',
                legal: 'Google Play und das Google Play Logo sind Marken von Google LLC.'
            },
            contact: {
                title: 'Schreib uns',
                lede: "W\xe4hl ein Thema, und dein E-Mail-Programm \xf6ffnet sich mit fertigem Betreff. Jede Nachricht liest ein echter Mensch.",
                close: "Schlie\xdfen",
                feedbackTitle: 'Feedback oder Ideen',
                feedbackBody: "Was dir gef\xe4llt, was du \xe4ndern w\xfcrdest",
                bugTitle: 'Etwas funktioniert nicht',
                bugBody: "Nenn uns das Spiel und dein Ger\xe4t",
                dataTitle: 'Meine Daten',
                dataBody: "Einsehen oder l\xf6schen, was wir speichern",
                orWrite: 'Oder schreib uns direkt:',
                copy: 'Kopieren',
                copied: 'Kopiert'
            },
            ui: {
                languageLabel: 'Sprache'
            }
        },
        fr: {
            meta: {
                title: 'nocap.gg - Choisissez un jeu. Jouez tout de suite.',
                description: "Des jeux de navigateur que nous cr\xe9ons nous-m\xeames. Parties rapides, vrais adversaires, rien \xe0 installer."
            },
            nav: {
                aria: 'Navigation principale',
                ourGames: 'Nos jeux'
            },
            hero: {
                titleLine1: 'Choisissez un jeu.',
                titleLine2: 'Jouez tout de suite.',
                cta: 'Voir nos jeux'
            },
            games: {
                heading: 'Nos jeux'
            },
            boardbash: {
                logoAlt: 'Logo de Board Bash',
                tagline: "Jeux de soci\xe9t\xe9 classiques, parties rapides",
                body: "Morpion, Quatre align\xe9s, Pipopipette et Gomoku. Jouez contre l\u2019ordinateur, passez l\u2019appareil, ou ouvrez une salle pour affronter un ami.",
                tagTicTacToe: 'Morpion',
                tagFourInARow: "Quatre align\xe9s",
                tagDotsAndBoxes: 'Pipopipette',
                tagGomoku: 'Gomoku',
                play: "Jouer \xe0 Board Bash",
                storeAria: "T\xe9l\xe9charger Board Bash sur Google Play",
                players: "1\u20132 joueurs \xb7 en ligne et local"
            },
            lagerhaus: {
                tagline: "Casse-t\xeate d\u2019entrep\xf4t",
                body: "Sokoban, en beaut\xe9. Poussez chaque caisse sur sa marque en un minimum de coups, puis placez votre score au classement en ligne. Des niveaux uniques \xe0 l\u2019infini : aucune partie ne ressemble \xe0 une autre.",
                tagPuzzle: "Casse-t\xeate",
                tagSolo: 'Solo',
                tagLeaderboards: 'Classements',
                play: "Jouer \xe0 Lagerhaus",
                storeAria: "T\xe9l\xe9charger Lagerhaus sur Google Play",
                players: "1 joueur \xb7 web et mobile"
            },
            store: {
                badgeAlt: 'Disponible sur Google Play'
            },
            workshop: {
                heading: "D\u2019autres de nos jeux sont \xe0 l\u2019atelier.",
                body: "Un jeu n\u2019arrive ici que lorsque nous en sommes fiers : les nouveaux titres sortent d\xe8s qu\u2019ils sont pr\xeats \xe0 jouer."
            },
            footer: {
                games: 'Jeux',
                privacy: "Confidentialit\xe9",
                contact: 'Contact',
                madeWith: "Fait avec \u2764\uFE0F en",
                germanEngineered: "Ing\xe9nierie allemande",
                australia: 'Australie',
                legal: 'Google Play et le logo Google Play sont des marques de Google LLC.'
            },
            contact: {
                title: "\xc9crivez-nous",
                lede: "Choisissez le sujet et votre messagerie s\u2019ouvre avec l\u2019objet d\xe9j\xe0 rempli. Une vraie personne lit chaque message.",
                close: 'Fermer',
                feedbackTitle: "Avis ou id\xe9es",
                feedbackBody: 'Ce que vous aimez, ce que vous changeriez',
                bugTitle: 'Quelque chose ne marche pas',
                bugBody: 'Indiquez le jeu et votre appareil',
                dataTitle: "Mes donn\xe9es",
                dataBody: 'Consulter ou supprimer ce que nous gardons',
                orWrite: "Ou \xe9crivez-nous directement :",
                copy: 'Copier',
                copied: "Copi\xe9"
            },
            ui: {
                languageLabel: 'Langue'
            }
        },
        it: {
            meta: {
                title: 'nocap.gg - Scegli un gioco. Gioca subito.',
                description: 'Giochi da browser che realizziamo noi. Partite veloci, avversari reali, niente da installare.'
            },
            nav: {
                aria: 'Navigazione principale',
                ourGames: 'I nostri giochi'
            },
            hero: {
                titleLine1: 'Scegli un gioco.',
                titleLine2: 'Gioca subito.',
                cta: 'Guarda i nostri giochi'
            },
            games: {
                heading: 'I nostri giochi'
            },
            boardbash: {
                logoAlt: 'Logo di Board Bash',
                tagline: 'Giochi da tavolo classici, partite veloci',
                body: 'Tris, Quattro in fila, Punti e quadrati e Gomoku. Gioca contro il computer, passa il dispositivo, o apri una stanza e sfida un amico.',
                tagTicTacToe: 'Tris',
                tagFourInARow: 'Quattro in fila',
                tagDotsAndBoxes: 'Punti e quadrati',
                tagGomoku: 'Gomoku',
                play: 'Gioca a Board Bash',
                storeAria: 'Scarica Board Bash su Google Play',
                players: "1\u20132 giocatori \xb7 online e locale"
            },
            lagerhaus: {
                tagline: 'Rompicapo in magazzino',
                body: "Sokoban, fatto bene. Spingi ogni cassa sul suo segno con il minor numero di mosse, poi porta il tuo punteggio nella classifica online. Livelli unici senza fine: nessuna partita \xe8 uguale all\u2019altra.",
                tagPuzzle: 'Rompicapo',
                tagSolo: 'Solo',
                tagLeaderboards: 'Classifiche',
                play: 'Gioca a Lagerhaus',
                storeAria: 'Scarica Lagerhaus su Google Play',
                players: "1 giocatore \xb7 web e mobile"
            },
            store: {
                badgeAlt: 'Disponibile su Google Play'
            },
            workshop: {
                heading: 'Altri nostri giochi sono in officina.',
                body: "Un gioco arriva qui solo quando ne siamo fieri, cos\xec i nuovi titoli escono appena sono pronti da giocare."
            },
            footer: {
                games: 'Giochi',
                privacy: 'Privacy',
                contact: 'Contatti',
                madeWith: "Fatto con \u2764\uFE0F in",
                germanEngineered: 'Ingegneria tedesca',
                australia: 'Australia',
                legal: 'Google Play e il logo Google Play sono marchi di Google LLC.'
            },
            contact: {
                title: 'Scrivici',
                lede: "Scegli l\u2019argomento e la tua app di posta si apre con l\u2019oggetto gi\xe0 compilato. Ogni messaggio lo legge una persona vera.",
                close: 'Chiudi',
                feedbackTitle: 'Feedback o idee',
                feedbackBody: 'Cosa ti piace, cosa cambieresti',
                bugTitle: 'Qualcosa non funziona',
                bugBody: 'Dicci il gioco e il tuo dispositivo',
                dataTitle: 'I miei dati',
                dataBody: "Vedi o elimina ci\xf2 che conserviamo",
                orWrite: 'Oppure scrivici direttamente:',
                copy: 'Copia',
                copied: 'Copiato'
            },
            ui: {
                languageLabel: 'Lingua'
            }
        },
        es: {
            meta: {
                title: 'nocap.gg - Elige un juego. Juega ahora mismo.',
                description: "Juegos de navegador que hacemos nosotros. Partidas r\xe1pidas, rivales reales, nada que instalar."
            },
            nav: {
                aria: "Navegaci\xf3n principal",
                ourGames: 'Nuestros juegos'
            },
            hero: {
                titleLine1: 'Elige un juego.',
                titleLine2: 'Juega ahora mismo.',
                cta: 'Ver nuestros juegos'
            },
            games: {
                heading: 'Nuestros juegos'
            },
            boardbash: {
                logoAlt: 'Logotipo de Board Bash',
                tagline: "Juegos de mesa cl\xe1sicos, partidas r\xe1pidas",
                body: "Tres en raya, Cuatro en l\xednea, Puntos y cajas y Gomoku. Juega contra la m\xe1quina, pasa el dispositivo, o abre una sala y reta a un amigo.",
                tagTicTacToe: 'Tres en raya',
                tagFourInARow: "Cuatro en l\xednea",
                tagDotsAndBoxes: 'Puntos y cajas',
                tagGomoku: 'Gomoku',
                play: 'Jugar a Board Bash',
                storeAria: 'Consigue Board Bash en Google Play',
                players: "1\u20132 jugadores \xb7 en l\xednea y local"
            },
            lagerhaus: {
                tagline: "Puzle de almac\xe9n",
                body: "Sokoban, bien hecho. Empuja cada caja hasta su marca con los menos movimientos posibles y lleva tu puntuaci\xf3n a la clasificaci\xf3n en l\xednea. Niveles \xfanicos infinitos: ninguna partida se parece a otra.",
                tagPuzzle: 'Puzle',
                tagSolo: 'Individual',
                tagLeaderboards: 'Clasificaciones',
                play: 'Jugar a Lagerhaus',
                storeAria: 'Consigue Lagerhaus en Google Play',
                players: "1 jugador \xb7 web y m\xf3vil"
            },
            store: {
                badgeAlt: 'Disponible en Google Play'
            },
            workshop: {
                heading: "Tenemos m\xe1s juegos en el taller.",
                body: "Un juego solo llega aqu\xed cuando estamos orgullosos de \xe9l, as\xed que los nuevos t\xedtulos aparecen en cuanto est\xe1n listos para jugar."
            },
            footer: {
                games: 'Juegos',
                privacy: 'Privacidad',
                contact: 'Contacto',
                madeWith: "Hecho con \u2764\uFE0F en",
                germanEngineered: "Ingenier\xeda alemana",
                australia: 'Australia',
                legal: 'Google Play y el logotipo de Google Play son marcas de Google LLC.'
            },
            contact: {
                title: "Escr\xedbenos",
                lede: 'Elige el tema y tu app de correo se abre con el asunto ya escrito. Cada mensaje lo lee una persona real.',
                close: 'Cerrar',
                feedbackTitle: 'Opiniones o ideas',
                feedbackBody: "Lo que te gusta, lo que cambiar\xedas",
                bugTitle: 'Algo no funciona',
                bugBody: 'Dinos el juego y tu dispositivo',
                dataTitle: 'Mis datos',
                dataBody: 'Consulta o borra lo que guardamos',
                orWrite: "O escr\xedbenos directamente:",
                copy: 'Copiar',
                copied: 'Copiado'
            },
            ui: {
                languageLabel: 'Idioma'
            }
        },
        pl: {
            meta: {
                title: "nocap.gg - Wybierz gr\u0119. Zagraj od razu.",
                description: "Gry przegl\u0105darkowe, kt\xf3re robimy sami. Szybkie rundy, prawdziwi przeciwnicy, nic do instalowania."
            },
            nav: {
                aria: "Nawigacja g\u0142\xf3wna",
                ourGames: 'Nasze gry'
            },
            hero: {
                titleLine1: "Wybierz gr\u0119.",
                titleLine2: 'Zagraj od razu.',
                cta: 'Zobacz nasze gry'
            },
            games: {
                heading: 'Nasze gry'
            },
            boardbash: {
                logoAlt: 'Logo Board Bash',
                tagline: 'Klasyczne gry planszowe, szybkie rundy',
                body: "K\xf3\u0142ko i krzy\u017Cyk, Cztery w rz\u0119dzie, Kropki i kwadraty oraz Gomoku. Zagraj z komputerem, podaj urz\u0105dzenie dalej, albo otw\xf3rz pok\xf3j i zmierz si\u0119 ze znajomym.",
                tagTicTacToe: "K\xf3\u0142ko i krzy\u017Cyk",
                tagFourInARow: "Cztery w rz\u0119dzie",
                tagDotsAndBoxes: 'Kropki i kwadraty',
                tagGomoku: 'Gomoku',
                play: 'Zagraj w Board Bash',
                storeAria: 'Pobierz Board Bash z Google Play',
                players: "1\u20132 gracze \xb7 online i lokalnie"
            },
            lagerhaus: {
                tagline: "\u0141amig\u0142\xf3wka magazynowa",
                body: "Sokoban zrobiony z klas\u0105. Przepchnij ka\u017Cd\u0105 skrzyni\u0119 na jej miejsce w jak najmniejszej liczbie ruch\xf3w, a potem wy\u015Blij wynik na rankingi online. Niesko\u0144czone, unikalne poziomy \u2013 \u017Cadna runda nie jest taka sama.",
                tagPuzzle: "\u0141amig\u0142\xf3wka",
                tagSolo: 'Solo',
                tagLeaderboards: 'Rankingi',
                play: 'Zagraj w Lagerhaus',
                storeAria: 'Pobierz Lagerhaus z Google Play',
                players: "1 gracz \xb7 web i mobile"
            },
            store: {
                badgeAlt: 'Pobierz z Google Play'
            },
            workshop: {
                heading: "Wi\u0119cej naszych gier jest w warsztacie.",
                body: "Gra trafia tutaj dopiero wtedy, gdy jeste\u015Bmy z niej dumni \u2013 nowe tytu\u0142y pojawiaj\u0105 si\u0119, gdy s\u0105 gotowe do gry."
            },
            footer: {
                games: 'Gry',
                privacy: "Prywatno\u015B\u0107",
                contact: 'Kontakt',
                madeWith: "Zrobione z \u2764\uFE0F w",
                germanEngineered: "Niemiecka in\u017Cynieria",
                australia: 'Australii',
                legal: "Google Play i logo Google Play s\u0105 znakami towarowymi Google LLC."
            },
            contact: {
                title: 'Napisz do nas',
                lede: "Wybierz temat, a Twoja aplikacja pocztowa otworzy si\u0119 z gotowym tytu\u0142em wiadomo\u015Bci. Ka\u017Cd\u0105 wiadomo\u015B\u0107 czyta prawdziwy cz\u0142owiek.",
                close: 'Zamknij',
                feedbackTitle: "Opinie lub pomys\u0142y",
                feedbackBody: "Co Ci si\u0119 podoba, a co warto zmieni\u0107",
                bugTitle: "Co\u015B nie dzia\u0142a",
                bugBody: "Podaj gr\u0119 i swoje urz\u0105dzenie",
                dataTitle: 'Moje dane',
                dataBody: "Sprawd\u017A lub usu\u0144 to, co przechowujemy",
                orWrite: "Albo napisz bezpo\u015Brednio:",
                copy: 'Kopiuj',
                copied: 'Skopiowano'
            },
            ui: {
                languageLabel: "J\u0119zyk"
            }
        },
        cs: {
            meta: {
                title: 'nocap.gg - Vyber si hru. Hraj hned.',
                description: "Prohl\xed\u017Ee\u010Dov\xe9 hry, kter\xe9 tvo\u0159\xedme sami. Kr\xe1tk\xe9 partie, skute\u010Dn\xed protivn\xedci, nic se neinstaluje."
            },
            nav: {
                aria: "Hlavn\xed navigace",
                ourGames: "Na\u0161e hry"
            },
            hero: {
                titleLine1: 'Vyber si hru.',
                titleLine2: 'Hraj hned.',
                cta: "Prohl\xe9dnout na\u0161e hry"
            },
            games: {
                heading: "Na\u0161e hry"
            },
            boardbash: {
                logoAlt: 'Logo Board Bash',
                tagline: "Klasick\xe9 deskov\xe9 hry, rychl\xe9 partie",
                body: "Pi\u0161kvorky, \u010Cty\u0159i v \u0159ad\u011B, Te\u010Dky a \u010Dtverce a Gomoku. Hraj proti po\u010D\xedta\u010Di, pos\xedlej za\u0159\xedzen\xed kolem, nebo otev\u0159i m\xedstnost a vyzvi p\u0159\xedtele.",
                tagTicTacToe: "Pi\u0161kvorky",
                tagFourInARow: "\u010Cty\u0159i v \u0159ad\u011B",
                tagDotsAndBoxes: "Te\u010Dky a \u010Dtverce",
                tagGomoku: 'Gomoku',
                play: "Hr\xe1t Board Bash",
                storeAria: "Z\xedskat Board Bash na Google Play",
                players: "1\u20132 hr\xe1\u010Di \xb7 online i lok\xe1ln\u011B"
            },
            lagerhaus: {
                tagline: "Skladov\xe1 h\xe1danka",
                body: "Sokoban, jak m\xe1 b\xfdt. Dostrkej ka\u017Edou bednu na jej\xed m\xedsto na co nejmen\u0161\xed po\u010Det tah\u016F a pak po\u0161li sk\xf3re do online \u017Eeb\u0159\xed\u010Dku. Nekone\u010Dn\u011B unik\xe1tn\xedch \xfarovn\xed \u2013 \u017E\xe1dn\xe1 partie nen\xed stejn\xe1.",
                tagPuzzle: "H\xe1danka",
                tagSolo: 'Solo',
                tagLeaderboards: "\u017Deb\u0159\xed\u010Dky",
                play: "Hr\xe1t Lagerhaus",
                storeAria: "Z\xedskat Lagerhaus na Google Play",
                players: "1 hr\xe1\u010D \xb7 web i mobil"
            },
            store: {
                badgeAlt: "Nyn\xed na Google Play"
            },
            workshop: {
                heading: "Dal\u0161\xed na\u0161e hry jsou v d\xedln\u011B.",
                body: "Hra se sem dostane teprve tehdy, kdy\u017E jsme na ni py\u0161n\xed \u2013 nov\xe9 tituly tak p\u0159ich\xe1zej\xed, jakmile jsou hrateln\xe9."
            },
            footer: {
                games: 'Hry',
                privacy: "Soukrom\xed",
                contact: 'Kontakt',
                madeWith: "Vyrobeno s \u2764\uFE0F v",
                germanEngineered: "N\u011Bmeck\xe9 in\u017Een\xfdrstv\xed",
                australia: "Austr\xe1lii",
                legal: "Google Play a logo Google Play jsou obchodn\xed zn\xe1mky spole\u010Dnosti Google LLC."
            },
            contact: {
                title: "Napi\u0161 n\xe1m",
                lede: "Vyber t\xe9ma a otev\u0159e se ti e-mail s vypln\u011Bn\xfdm p\u0159edm\u011Btem. Ka\u017Edou zpr\xe1vu \u010Dte skute\u010Dn\xfd \u010Dlov\u011Bk.",
                close: "Zav\u0159\xedt",
                feedbackTitle: "N\xe1zory nebo n\xe1pady",
                feedbackBody: "Co se ti l\xedb\xed a co zm\u011Bnit",
                bugTitle: "N\u011Bco nefunguje",
                bugBody: "Napi\u0161 n\xe1m hru a sv\xe9 za\u0159\xedzen\xed",
                dataTitle: 'Moje data',
                dataBody: "Zobrazit nebo smazat, co uchov\xe1v\xe1me",
                orWrite: "Nebo n\xe1m napi\u0161 p\u0159\xedmo:",
                copy: "Kop\xedrovat",
                copied: "Zkop\xedrov\xe1no"
            },
            ui: {
                languageLabel: 'Jazyk'
            }
        },
        mk: {
            meta: {
                title: "nocap.gg - \u0418\u0437\u0431\u0435\u0440\u0438 \u0438\u0433\u0440\u0430. \u0418\u0433\u0440\u0430\u0458 \u0432\u0435\u0434\u043D\u0430\u0448.",
                description: "\u0418\u0433\u0440\u0438 \u0432\u043E \u043F\u0440\u0435\u043B\u0438\u0441\u0442\u0443\u0432\u0430\u0447 \u0448\u0442\u043E \u0433\u0438 \u043F\u0440\u0430\u0432\u0438\u043C\u0435 \u0441\u0430\u043C\u0438. \u041A\u0440\u0430\u0442\u043A\u0438 \u043F\u0430\u0440\u0442\u0438\u0438, \u0432\u0438\u0441\u0442\u0438\u043D\u0441\u043A\u0438 \u043F\u0440\u043E\u0442\u0438\u0432\u043D\u0438\u0446\u0438, \u043D\u0438\u0448\u0442\u043E \u0437\u0430 \u0438\u043D\u0441\u0442\u0430\u043B\u0438\u0440\u0430\u045A\u0435."
            },
            nav: {
                aria: "\u0413\u043B\u0430\u0432\u043D\u0430 \u043D\u0430\u0432\u0438\u0433\u0430\u0446\u0438\u0458\u0430",
                ourGames: "\u041D\u0430\u0448\u0438\u0442\u0435 \u0438\u0433\u0440\u0438"
            },
            hero: {
                titleLine1: "\u0418\u0437\u0431\u0435\u0440\u0438 \u0438\u0433\u0440\u0430.",
                titleLine2: "\u0418\u0433\u0440\u0430\u0458 \u0432\u0435\u0434\u043D\u0430\u0448.",
                cta: "\u0412\u0438\u0434\u0438 \u0433\u0438 \u043D\u0430\u0448\u0438\u0442\u0435 \u0438\u0433\u0440\u0438"
            },
            games: {
                heading: "\u041D\u0430\u0448\u0438\u0442\u0435 \u0438\u0433\u0440\u0438"
            },
            boardbash: {
                logoAlt: "\u041B\u043E\u0433\u043E\u0442\u043E \u043D\u0430 Board Bash",
                tagline: "\u041A\u043B\u0430\u0441\u0438\u0447\u043D\u0438 \u0438\u0433\u0440\u0438 \u043D\u0430 \u0442\u0430\u0431\u043B\u0430, \u043A\u0440\u0430\u0442\u043A\u0438 \u043F\u0430\u0440\u0442\u0438\u0438",
                body: "\u0418\u043A\u0441-\u043E\u043A\u0441, \u0427\u0435\u0442\u0438\u0440\u0438 \u0432\u043E \u0440\u0435\u0434, \u0422\u043E\u0447\u043A\u0438 \u0438 \u043A\u0432\u0430\u0434\u0440\u0430\u0442\u0438 \u0438 \u0413\u043E\u043C\u043E\u043A\u0443. \u0418\u0433\u0440\u0430\u0458 \u043F\u0440\u043E\u0442\u0438\u0432 \u043A\u043E\u043C\u043F\u0458\u0443\u0442\u0435\u0440\u043E\u0442, \u043F\u0440\u0435\u0434\u0430\u0432\u0430\u0458 \u0433\u043E \u0443\u0440\u0435\u0434\u043E\u0442, \u0438\u043B\u0438 \u043E\u0442\u0432\u043E\u0440\u0438 \u0441\u043E\u0431\u0430 \u0438 \u0441\u043F\u0440\u043E\u0442\u0438\u0432\u0441\u0442\u0430\u0432\u0438 \u0441\u0435 \u043D\u0430 \u043F\u0440\u0438\u0458\u0430\u0442\u0435\u043B.",
                tagTicTacToe: "\u0418\u043A\u0441-\u043E\u043A\u0441",
                tagFourInARow: "\u0427\u0435\u0442\u0438\u0440\u0438 \u0432\u043E \u0440\u0435\u0434",
                tagDotsAndBoxes: "\u0422\u043E\u0447\u043A\u0438 \u0438 \u043A\u0432\u0430\u0434\u0440\u0430\u0442\u0438",
                tagGomoku: "\u0413\u043E\u043C\u043E\u043A\u0443",
                play: "\u0418\u0433\u0440\u0430\u0458 Board Bash",
                storeAria: "\u041F\u0440\u0435\u0437\u0435\u043C\u0438 Board Bash \u043E\u0434 Google Play",
                players: "1\u20132 \u0438\u0433\u0440\u0430\u0447\u0438 \xb7 \u043E\u043D\u043B\u0430\u0458\u043D \u0438 \u043B\u043E\u043A\u0430\u043B\u043D\u043E"
            },
            lagerhaus: {
                tagline: "\u0421\u043A\u043B\u0430\u0434\u0438\u0448\u043D\u0430 \u0437\u0430\u0433\u0430\u0442\u043A\u0430",
                body: "\u0421\u043E\u043A\u043E\u0431\u0430\u043D, \u043D\u0430\u043F\u0440\u0430\u0432\u0435\u043D \u0443\u0431\u0430\u0432\u043E. \u041F\u043E\u0442\u0438\u0441\u043D\u0438 \u0441\u0435\u043A\u043E\u0458\u0430 \u043A\u0443\u0442\u0438\u0458\u0430 \u0434\u043E \u0441\u0432\u043E\u0435\u0442\u043E \u043C\u0435\u0441\u0442\u043E \u0441\u043E \u0448\u0442\u043E \u043F\u043E\u043C\u0430\u043B\u043A\u0443 \u0434\u0432\u0438\u0436\u0435\u045A\u0430, \u043F\u0430 \u043E\u0434\u043D\u0435\u0441\u0438 \u0433\u043E \u0440\u0435\u0437\u0443\u043B\u0442\u0430\u0442\u043E\u0442 \u043D\u0430 \u043E\u043D\u043B\u0430\u0458\u043D \u0440\u0430\u043D\u0433-\u043B\u0438\u0441\u0442\u0430\u0442\u0430. \u0411\u0435\u0437\u0431\u0440\u043E\u0458 \u0443\u043D\u0438\u043A\u0430\u0442\u043D\u0438 \u043D\u0438\u0432\u043E\u0430 \u2013 \u043D\u0438\u0435\u0434\u043D\u0430 \u043F\u0430\u0440\u0442\u0438\u0458\u0430 \u043D\u0435 \u0435 \u0438\u0441\u0442\u0430.",
                tagPuzzle: "\u0417\u0430\u0433\u0430\u0442\u043A\u0430",
                tagSolo: "\u0421\u043E\u043B\u043E",
                tagLeaderboards: "\u0420\u0430\u043D\u0433-\u043B\u0438\u0441\u0442\u0438",
                play: "\u0418\u0433\u0440\u0430\u0458 Lagerhaus",
                storeAria: "\u041F\u0440\u0435\u0437\u0435\u043C\u0438 Lagerhaus \u043E\u0434 Google Play",
                players: "1 \u0438\u0433\u0440\u0430\u0447 \xb7 \u0432\u0435\u0431 \u0438 \u043C\u043E\u0431\u0438\u043B\u0435\u043D"
            },
            store: {
                badgeAlt: "\u041F\u0440\u0435\u0437\u0435\u043C\u0438 \u043E\u0434 Google Play"
            },
            workshop: {
                heading: "\u0423\u0448\u0442\u0435 \u043D\u0430\u0448\u0438 \u0438\u0433\u0440\u0438 \u0441\u0435 \u0432\u043E \u0440\u0430\u0431\u043E\u0442\u0438\u043B\u043D\u0438\u0446\u0430\u0442\u0430.",
                body: "\u0418\u0433\u0440\u0430\u0442\u0430 \u0441\u0442\u0438\u0433\u043D\u0443\u0432\u0430 \u0442\u0443\u043A\u0430 \u0441\u0430\u043C\u043E \u043A\u043E\u0433\u0430 \u0441\u043C\u0435 \u0433\u043E\u0440\u0434\u0438 \u043D\u0430 \u043D\u0435\u0430, \u0442\u0430\u043A\u0430 \u0448\u0442\u043E \u043D\u043E\u0432\u0438\u0442\u0435 \u043D\u0430\u0441\u043B\u043E\u0432\u0438 \u0438\u0437\u043B\u0435\u0433\u0443\u0432\u0430\u0430\u0442 \u0448\u0442\u043E\u043C \u0441\u0435 \u0433\u043E\u0442\u043E\u0432\u0438 \u0437\u0430 \u0438\u0433\u0440\u0430\u045A\u0435."
            },
            footer: {
                games: "\u0418\u0433\u0440\u0438",
                privacy: "\u041F\u0440\u0438\u0432\u0430\u0442\u043D\u043E\u0441\u0442",
                contact: "\u041A\u043E\u043D\u0442\u0430\u043A\u0442",
                madeWith: "\u041D\u0430\u043F\u0440\u0430\u0432\u0435\u043D\u043E \u0441\u043E \u2764\uFE0F \u0432\u043E",
                germanEngineered: "\u0413\u0435\u0440\u043C\u0430\u043D\u0441\u043A\u043E \u0438\u043D\u0436\u0435\u043D\u0435\u0440\u0441\u0442\u0432\u043E",
                australia: "\u0410\u0432\u0441\u0442\u0440\u0430\u043B\u0438\u0458\u0430",
                legal: "Google Play \u0438 \u043B\u043E\u0433\u043E\u0442\u043E \u043D\u0430 Google Play \u0441\u0435 \u0442\u0440\u0433\u043E\u0432\u0441\u043A\u0438 \u043C\u0430\u0440\u043A\u0438 \u043D\u0430 Google LLC."
            },
            contact: {
                title: "\u041F\u0438\u0448\u0438 \u043D\u0438",
                lede: "\u0418\u0437\u0431\u0435\u0440\u0438 \u0442\u0435\u043C\u0430 \u0438 \u0442\u0432\u043E\u0458\u0430\u0442\u0430 \u0430\u043F\u043B\u0438\u043A\u0430\u0446\u0438\u0458\u0430 \u0437\u0430 \u0435-\u043F\u043E\u0448\u0442\u0430 \u045C\u0435 \u0441\u0435 \u043E\u0442\u0432\u043E\u0440\u0438 \u0441\u043E \u043F\u043E\u043F\u043E\u043B\u043D\u0435\u0442 \u043D\u0430\u0441\u043B\u043E\u0432. \u0421\u0435\u043A\u043E\u0458\u0430 \u043F\u043E\u0440\u0430\u043A\u0430 \u0458\u0430 \u0447\u0438\u0442\u0430 \u0432\u0438\u0441\u0442\u0438\u043D\u0441\u043A\u0438 \u0447\u043E\u0432\u0435\u043A.",
                close: "\u0417\u0430\u0442\u0432\u043E\u0440\u0438",
                feedbackTitle: "\u041C\u0438\u0441\u043B\u0435\u045A\u0430 \u0438\u043B\u0438 \u0438\u0434\u0435\u0438",
                feedbackBody: "\u0428\u0442\u043E \u0442\u0438 \u0441\u0435 \u0434\u043E\u043F\u0430\u0453\u0430, \u0430 \u0448\u0442\u043E \u0434\u0430 \u0441\u043C\u0435\u043D\u0438\u043C\u0435",
                bugTitle: "\u041D\u0435\u0448\u0442\u043E \u043D\u0435 \u0440\u0430\u0431\u043E\u0442\u0438",
                bugBody: "\u041A\u0430\u0436\u0438 \u043D\u0438 \u0458\u0430 \u0438\u0433\u0440\u0430\u0442\u0430 \u0438 \u0443\u0440\u0435\u0434\u043E\u0442",
                dataTitle: "\u041C\u043E\u0438\u0442\u0435 \u043F\u043E\u0434\u0430\u0442\u043E\u0446\u0438",
                dataBody: "\u0412\u0438\u0434\u0438 \u0438\u043B\u0438 \u0438\u0437\u0431\u0440\u0438\u0448\u0438 \u0448\u0442\u043E \u0447\u0443\u0432\u0430\u043C\u0435",
                orWrite: "\u0418\u043B\u0438 \u043F\u0438\u0448\u0438 \u043D\u0438 \u0434\u0438\u0440\u0435\u043A\u0442\u043D\u043E:",
                copy: "\u041A\u043E\u043F\u0438\u0440\u0430\u0458",
                copied: "\u041A\u043E\u043F\u0438\u0440\u0430\u043D\u043E"
            },
            ui: {
                languageLabel: "\u0408\u0430\u0437\u0438\u043A"
            }
        }
    }
};

//# sourceMappingURL=nocap-portal.07c5a148.js.map
